import axios from 'axios';
import { Store } from 'vuex';
import VueRouter from 'vue-router';
import TranslationService from '@/locale/translation.service';

export default class AccountService {
    constructor(private store: Store<any>, private translationService: TranslationService, private router: VueRouter) {
        this.init();
    }

    public init(): void {
        this.retrieveProfiles();
    }

    public retrieveProfiles(): Promise<boolean> {
        return new Promise(resolve => {
            axios
                .get('management/info')
                .then(res => {
                    if (res.data && res.data.activeProfiles) {
                        this.store.commit('setRibbonOnProfiles', res.data['display-ribbon-on-profiles']);
                        this.store.commit('setActiveProfiles', res.data['activeProfiles']);
                        this.store.commit('setVersion', res.data['version']);
                    }
                    resolve(true);
                })
                .catch(() => resolve(false));
        });
    }

    public retrieveAccount(): Promise<boolean> {
        return new Promise(resolve => {
            const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
            if (!token) {
                return resolve(false);
            }
            axios
                .get('api/account')
                .then(response => {
                    this.store.commit('authenticate');
                    const account = response.data;
                    if (account) {
                        this.store.commit('authenticated', account);
                        if (this.store.getters.currentLanguage !== account.langKey) {
                            this.store.commit('currentLanguage', account.langKey);
                        }
                        if (sessionStorage.getItem('requested-url')) {
                            // Swallow benign "navigation cancelled/duplicated" rejections (vue-router 3).
                            this.router.replace(sessionStorage.getItem('requested-url')).catch(() => undefined);
                            sessionStorage.removeItem('requested-url');
                        }
                    } else {
                        this.store.commit('logout');
                        this.router.push('/').catch(() => undefined);
                        sessionStorage.removeItem('requested-url');
                    }
                    this.translationService.refreshTranslation(this.store.getters.currentLanguage);
                    resolve(true);
                })
                .catch(() => {
                    this.store.commit('logout');
                    resolve(false);
                });
        });
    }

    public async getAuthorities(): Promise<string[]> {
        if (!this.authenticated || !this.userAuthorities) {
            const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
            if (!this.store.getters.account && !this.store.getters.logon && token) {
                return this.retrieveAccount().then(() => this.getAuthorities());
            } else {
                return [];
            }
        }

        return this.userAuthorities;
    }

    public hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
        if (typeof authorities === 'string') {
            authorities = [authorities];
        }

        if (!this.authenticated || !this.userAuthorities) {
            if (!this.store.getters.account && !this.store.getters.logon) {
                return this.retrieveAccount();
            } else {
                return new Promise(resolve => {
                    resolve(false);
                });
            }
        }

        for (let i = 0; i < authorities.length; i++) {
            if (this.userAuthorities.includes(authorities[i])) {
                return new Promise(resolve => {
                    resolve(true);
                });
            }
        }

        return new Promise(resolve => {
            resolve(false);
        });
    }

    public async requestPasswordReset(username) {
        try {
            const response = await axios.post('api/account/reset-password/init', { username });
            return response.status === 200;
        } catch (e) {
            return false;
        }
    }

    public get authenticated(): boolean {
        return this.store.getters.authenticated;
    }

    public get userAuthorities(): any {
        return this.store.getters.account.authorities;
    }
}
